/**
 * Field Definitions Service
 * Manages field definitions for data contracts with support for company-specific fields
 *
 * Features:
 * - System-wide and company-specific field definitions
 * - Full CRUD operations with validation
 * - Field type management (unified_field_type enum)
 * - Category-based organization
 * - Validation rules and constraints
 * - PII and sensitivity marking
 */

import {
  supabase,
  handleSupabaseArray,
  type Json,
  type Tables,
} from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import type { UnifiedFieldType } from '@xanectech/types';
import { fieldRegistry } from '@xanectech/types';
import type {
  FieldDefinition,
  FieldDefinitionWithCategory,
  CreateFieldRequest,
  UpdateFieldRequest,
  FieldFilters,
} from '@xanectech/types';

const logger = createLogger('Data - FieldDefinitionsService');

type FieldDefinitionRow = Tables<'field_definitions'>;
type CountryContextRule = NonNullable<
  FieldDefinition['country_context_rules']
>[string];

interface FieldDefinitionWithCategoryRow extends FieldDefinitionRow {
  category?: {
    id: string;
    name: string;
    category_type: string;
    icon: string | null;
  } | null;
}

export class FieldDefinitionsService {
  private static normalizeCountryContextRules(
    value: Json | null,
  ): Record<string, CountryContextRule> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return undefined;
    }

    const result: Record<string, CountryContextRule> = {};

    for (const [countryCode, rule] of Object.entries(value)) {
      if (!rule || typeof rule !== 'object' || Array.isArray(rule)) {
        continue;
      }

      const maybeCountryCode =
        'country_code' in rule && typeof rule.country_code === 'string'
          ? rule.country_code
          : countryCode;

      if (!maybeCountryCode) {
        continue;
      }

      const normalizedRule: CountryContextRule = {
        country_code: maybeCountryCode,
      };

      if ('is_required' in rule && typeof rule.is_required === 'boolean') {
        normalizedRule.is_required = rule.is_required;
      }

      if ('pattern' in rule && typeof rule.pattern === 'string') {
        normalizedRule.pattern = rule.pattern;
      }

      if (
        'validation_message' in rule &&
        typeof rule.validation_message === 'string'
      ) {
        normalizedRule.validation_message = rule.validation_message;
      }

      result[countryCode] = normalizedRule;
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  private static normalizeOptions(
    value: Json | null,
  ): FieldDefinition['options'] | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }

    const options: FieldDefinition['options'] = {};

    const addOption = (key: string, optionValue: Json | undefined) => {
      if (
        optionValue === null ||
        typeof optionValue === 'string' ||
        typeof optionValue === 'number' ||
        typeof optionValue === 'boolean'
      ) {
        options[key] = optionValue;
      }
    };

    const normalizeOptionItem = (item: Json, index: number) => {
      if (
        item === null ||
        typeof item === 'string' ||
        typeof item === 'number' ||
        typeof item === 'boolean'
      ) {
        const key = String(item);
        options[key || String(index)] = item;
        return;
      }

      if (typeof item === 'object' && !Array.isArray(item)) {
        const valueCandidate =
          'value' in item &&
          (typeof item.value === 'string' || typeof item.value === 'number')
            ? String(item.value)
            : undefined;

        const labelCandidate =
          'label' in item &&
          (typeof item.label === 'string' || typeof item.label === 'number')
            ? String(item.label)
            : undefined;

        if (valueCandidate || labelCandidate) {
          options[valueCandidate ?? labelCandidate ?? String(index)] =
            labelCandidate ?? valueCandidate ?? '';
        }
      }
    };

    if (Array.isArray(value)) {
      value.forEach((item, index) => normalizeOptionItem(item, index));
      return Object.keys(options).length > 0 ? options : undefined;
    }

    // Support nested option payloads such as { options: [...] }
    if ('options' in value) {
      const nested = value.options;
      if (Array.isArray(nested)) {
        nested.forEach((item, index) => normalizeOptionItem(item, index));
      } else if (
        nested &&
        typeof nested === 'object' &&
        !Array.isArray(nested)
      ) {
        for (const [key, optionValue] of Object.entries(nested)) {
          addOption(key, optionValue);
        }
      }
    }

    for (const [key, optionValue] of Object.entries(value)) {
      if (key === 'options') {
        continue;
      }

      addOption(key, optionValue);
    }

    return Object.keys(options).length > 0 ? options : undefined;
  }

  private static toUnifiedFieldType(
    value: FieldDefinitionRow['data_type'],
  ): UnifiedFieldType {
    // Map DB-only variant to supported domain variant
    if (value === 'address_autocomplete') {
      return 'address';
    }

    // If this is the only extra DB value, TS will narrow this to UnifiedFieldType
    return value as UnifiedFieldType;
  }

  private static toFieldDefinition(field: FieldDefinitionRow): FieldDefinition {
    return {
      ...field,
      data_type: this.toUnifiedFieldType(field.data_type),
      company_id: field.company_id ?? undefined,
      country_context_rules: this.normalizeCountryContextRules(
        field.country_context_rules,
      ),
      created_at: field.created_at ?? '',
      default_value: field.default_value ?? undefined,
      depends_on_field_id: field.depends_on_field_id ?? undefined,
      description: field.description ?? undefined,
      display_order: field.display_order ?? 0,
      field_group: field.field_group ?? undefined,
      help_text: field.help_text ?? undefined,
      is_active: field.is_active ?? true,
      is_pii: field.is_pii ?? false,
      is_required: field.is_required,
      is_sensitive: field.is_sensitive ?? false,
      is_system_field: field.is_system_field ?? false,
      max_length: field.max_length ?? undefined,
      max_value: field.max_value ?? undefined,
      min_length: field.min_length ?? undefined,
      min_value: field.min_value ?? undefined,
      options: this.normalizeOptions(field.options),
      pattern: field.pattern ?? undefined,
      placeholder: field.placeholder ?? undefined,
      requires_country_context: field.requires_country_context ?? false,
      updated_at: field.updated_at ?? '',
      validation_message: field.validation_message ?? undefined,
      is_exportable: field.is_exportable ?? true,
      is_importable: field.is_importable ?? true,
    };
  }

  private static isFieldWithCategory(
    field: FieldDefinitionWithCategoryRow,
  ): field is FieldDefinitionWithCategoryRow & {
    category: {
      id: string;
      name: string;
      category_type: string;
      icon: string;
    };
  } {
    return (
      typeof field.category?.id === 'string' &&
      typeof field.category.name === 'string' &&
      typeof field.category.category_type === 'string' &&
      typeof field.category.icon === 'string'
    );
  }

  /**
   * Get all field definitions with optional filtering
   */
  static async getFields(
    filters: FieldFilters = {},
  ): Promise<FieldDefinition[]> {
    let query = supabase
      .from('field_definitions')
      .select('*')
      .order('display_order', { ascending: true });

    // Apply filters
    if (filters.category_id) {
      query = query.eq('category_id', filters.category_id);
    }

    if (filters.company_id !== undefined) {
      if (filters.company_id) {
        query = query.eq('company_id', filters.company_id);
      } else {
        query = query.is('company_id', null);
      }
    }

    if (filters.data_type) {
      query = query.eq('data_type', filters.data_type);
    }

    if (filters.is_active !== undefined) {
      query = query.eq('is_active', filters.is_active);
    }

    if (filters.is_system_field !== undefined) {
      query = query.eq('is_system_field', filters.is_system_field);
    }

    if (filters.is_required !== undefined) {
      query = query.eq('is_required', filters.is_required);
    }

    if (filters.is_pii !== undefined) {
      query = query.eq('is_pii', filters.is_pii);
    }

    if (filters.search_term) {
      query = query.or(
        `field_name.ilike.%${filters.search_term}%,field_label.ilike.%${filters.search_term}%,description.ilike.%${filters.search_term}%`,
      );
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching fields:', error);
      throw new Error(`Failed to fetch fields: ${error.message}`);
    }

    return handleSupabaseArray(data).map((field) =>
      this.toFieldDefinition(field),
    );
  }

  /**
   * Get field definitions available to a specific company (system + company-specific)
   */
  static async getCompanyFields(companyId: string): Promise<FieldDefinition[]> {
    const { data, error } = await supabase
      .from('field_definitions')
      .select('*')
      .or(`company_id.is.null,company_id.eq.${companyId}`)
      .eq('is_active', true)
      .order('category_id', { ascending: true })
      .order('display_order', { ascending: true });

    if (error) {
      logger.error('Error fetching company fields:', error);
      throw new Error(`Failed to fetch company fields: ${error.message}`);
    }

    return handleSupabaseArray(data).map((field) =>
      this.toFieldDefinition(field),
    );
  }

  /**
   * Get field definitions with category information
   */
  static async getFieldsWithCategories(
    filters: FieldFilters = {},
  ): Promise<FieldDefinitionWithCategory[]> {
    let query = supabase
      .from('field_definitions')
      .select(
        `
        *,
        category:data_categories(
          id,
          name,
          category_type,
          icon
        )
      `,
      )
      .order('display_order', { ascending: true });

    // Apply the same filters as getFields
    if (filters.category_id) {
      query = query.eq('category_id', filters.category_id);
    }

    if (filters.company_id !== undefined) {
      if (filters.company_id) {
        query = query.eq('company_id', filters.company_id);
      } else {
        query = query.is('company_id', null);
      }
    }

    // Add other filters as needed...

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching fields with categories:', error);
      throw new Error(
        `Failed to fetch fields with categories: ${error.message}`,
      );
    }

    return handleSupabaseArray(data as FieldDefinitionWithCategoryRow[])
      .filter((field) => this.isFieldWithCategory(field))
      .map((field) => ({
        ...this.toFieldDefinition(field),
        category: field.category,
      }));
  }

  /**
   * Get fields for a specific category
   */
  static async getFieldsByCategory(
    categoryId: string,
  ): Promise<FieldDefinition[]> {
    return this.getFields({ category_id: categoryId, is_active: true });
  }

  /**
   * Get a specific field definition by ID
   */
  static async getFieldById(fieldId: string): Promise<FieldDefinition> {
    const { data, error } = await supabase
      .from('field_definitions')
      .select('*')
      .eq('id', fieldId)
      .single();

    if (error) {
      logger.error('Error fetching field:', error);
      throw new Error(`Failed to fetch field: ${error.message}`);
    }

    if (!data) {
      throw new Error('Field definition not found');
    }

    return this.toFieldDefinition(data);
  }

  /**
   * Create a new field definition
   */
  static async createField(
    fieldData: CreateFieldRequest,
  ): Promise<FieldDefinition> {
    // Validate field name uniqueness within category and company scope
    await this.validateFieldName(
      fieldData.field_name,
      fieldData.category_id,
      fieldData.company_id,
    );

    const { data, error } = await supabase
      .from('field_definitions')
      .insert({
        ...fieldData,
        is_system_field: !fieldData.company_id, // System fields have no company_id
        is_required: fieldData.is_required ?? false,
        is_sensitive: fieldData.is_sensitive ?? false,
        is_pii: fieldData.is_pii ?? false,
        display_order: fieldData.display_order ?? 0,
        requires_country_context: fieldData.requires_country_context ?? false,
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      logger.error('Error creating field:', error);
      throw new Error(`Failed to create field: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data returned when creating field');
    }

    return this.toFieldDefinition(data);
  }

  /**
   * Update an existing field definition
   */
  static async updateField(
    fieldId: string,
    updates: UpdateFieldRequest,
  ): Promise<FieldDefinition> {
    // If updating field_name, validate uniqueness
    if (updates.field_name) {
      const currentField = await this.getFieldById(fieldId);
      if (updates.field_name !== currentField.field_name) {
        await this.validateFieldName(
          updates.field_name,
          updates.category_id || currentField.category_id,
          currentField.company_id,
        );
      }
    }

    const { data, error } = await supabase
      .from('field_definitions')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', fieldId)
      .select()
      .single();

    if (error) {
      logger.error('Error updating field:', error);
      throw new Error(`Failed to update field: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data returned when updating field');
    }

    return this.toFieldDefinition(data);
  }

  /**
   * Delete a field definition (only non-system fields)
   */
  static async deleteField(fieldId: string): Promise<void> {
    // Check if it's a system field
    const field = await this.getFieldById(fieldId);
    if (field.is_system_field) {
      throw new Error('Cannot delete system fields');
    }

    const { error } = await supabase
      .from('field_definitions')
      .delete()
      .eq('id', fieldId);

    if (error) {
      logger.error('Error deleting field:', error);
      throw new Error(`Failed to delete field: ${error.message}`);
    }
  }

  /**
   * Activate/deactivate a field
   */
  static async toggleFieldStatus(
    fieldId: string,
    isActive: boolean,
  ): Promise<FieldDefinition> {
    return this.updateField(fieldId, { is_active: isActive });
  }

  /**
   * Reorder fields within a category
   */
  static async reorderFields(
    fieldUpdates: Array<{ id: string; display_order: number }>,
  ): Promise<void> {
    const updates = fieldUpdates.map((update) =>
      supabase
        .from('field_definitions')
        .update({
          display_order: update.display_order,
          updated_at: new Date().toISOString(),
        })
        .eq('id', update.id),
    );

    const results = await Promise.all(updates);

    // Check for any errors
    const errors = results.filter((result) => result.error);
    if (errors.length > 0) {
      const errorDetails = errors.map((err) => err.error?.message).join(', ');
      logger.error('Error reordering fields:', { details: errorDetails });
      throw new Error('Failed to reorder fields');
    }
  }

  /**
   * Duplicate a field definition (useful for creating company-specific versions)
   */
  static async duplicateField(
    fieldId: string,
    companyId: string,
    newName?: string,
    categoryId?: string,
  ): Promise<FieldDefinition> {
    const originalField = await this.getFieldById(fieldId);

    const duplicateData: CreateFieldRequest = {
      category_id: categoryId || originalField.category_id,
      field_name: newName || `${originalField.field_name}_copy`,
      field_label: originalField.field_label,
      description: originalField.description ?? undefined,
      data_type: originalField.data_type,
      is_required: originalField.is_required ?? undefined,
      is_sensitive: originalField.is_sensitive,
      is_pii: originalField.is_pii,
      default_value: originalField.default_value,
      placeholder: originalField.placeholder,
      help_text: originalField.help_text,
      min_length: originalField.min_length,
      max_length: originalField.max_length,
      min_value: originalField.min_value,
      max_value: originalField.max_value,
      pattern: originalField.pattern,
      validation_message: originalField.validation_message,
      options: originalField.options,
      display_order: originalField.display_order,
      field_group: originalField.field_group,
      requires_country_context: originalField.requires_country_context,
      country_context_rules: originalField.country_context_rules,
      company_id: companyId,
    };

    return this.createField(duplicateData);
  }

  /**
   * Validate field name uniqueness within category and company scope
   */
  private static async validateFieldName(
    fieldName: string,
    categoryId: string,
    companyId?: string,
  ): Promise<void> {
    let query = supabase
      .from('field_definitions')
      .select('id')
      .eq('field_name', fieldName)
      .eq('category_id', categoryId);

    if (companyId) {
      query = query.eq('company_id', companyId);
    } else {
      query = query.is('company_id', null);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error validating field name:', error);
      throw new Error(`Failed to validate field name: ${error.message}`);
    }

    if (data && data.length > 0) {
      throw new Error(
        `Field name '${fieldName}' already exists in this category`,
      );
    }
  }

  /**
   * Get field usage statistics
   */
  static async getFieldStats(fieldId: string): Promise<{
    contract_count: number;
  }> {
    // Count contracts using this field
    const { count, error } = await supabase
      .from('contract_field_requirements')
      .select('*', { count: 'exact', head: true })
      .eq('field_id', fieldId);

    if (error) {
      logger.error('Error counting field usage:', error);
    }

    return {
      contract_count: count || 0,
    };
  }

  /**
   * Get all available field types
   */
  static getAvailableFieldTypes(): Array<{
    value: UnifiedFieldType;
    label: string;
    category: 'data-entry' | 'selection' | 'layout';
    description: string;
  }> {
    return fieldRegistry.getAll().map((config) => ({
      value: config.type,
      label: config.label,
      category: config.category,
      description: config.description,
    }));
  }
}
